import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import {
  MdChair,
  MdFormatPaint,
  MdBorderOuter,
  MdClose,
  MdVisibility,
} from 'react-icons/md';
import { GiPillow } from 'react-icons/gi';
import type { RootState } from '../../../redux/store';
import type { FabricZone } from '../customizerConfig';
import type { TabId } from '../customizerConfig';
import {
  setFrameFabricUrl,
  setBackCushionFabricUrl,
  setSeatFabricUrl,
  setArmrestFabricUrl,
  setFinishUrl,
  setPillowFile,
  setPillowSize,
  setPillowCount,
  setPillowFabricUrl,
  setNailheadFile,
  setNailheadSize,
} from '../../../redux/slices/customizationSlice';
import { THEME, SPRING } from '../theme';
import SwatchPreviewModal from './SwatchPreviewModal';

interface SelectionItem {
  id: string;
  category: string;
  label: string;
  imageUrl?: string;
  isFile?: boolean;
  filePreviewUrl?: string;
  meta?: string;
  tabId: TabId;
  icon: React.ReactNode;
  clearAction: () => void;
}

interface SelectionsSummaryProps {
  fabricZones: FabricZone[];
  onNavigateTab?: (tab: TabId) => void;
}

const PARAM_TO_ACTION: Record<string, (val: null) => any> = {
  frameFabricUrl: (v) => setFrameFabricUrl(v),
  seatFabricUrl: (v) => setSeatFabricUrl(v),
  backCushionFabricUrl: (v) => setBackCushionFabricUrl(v),
  armrestFabricUrl: (v) => setArmrestFabricUrl(v),
};

const ZONE_ICONS: Record<string, React.ReactNode> = {
  frame: <MdChair size={11} />,
  seat: <MdChair size={11} />,
  back: <MdChair size={11} />,
  armrest: <MdChair size={11} />,
};

const SelectionsSummary: React.FC<SelectionsSummaryProps> = ({ fabricZones, onNavigateTab }) => {
  const dispatch = useDispatch();
  const customization = useSelector((s: RootState) => s.customization);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState<string>('');

  const selections = useMemo(() => {
    const items: SelectionItem[] = [];

    // Fabric selections
    fabricZones.forEach((zone) => {
      const url = (customization as any)[zone.paramKey] as string | null;
      if (url) {
        const actionCreator = PARAM_TO_ACTION[zone.paramKey];
        items.push({
          id: `fabric-${zone.id}`,
          category: 'Fabric',
          label: zone.label,
          imageUrl: url,
          tabId: 'fabric',
          icon: ZONE_ICONS[zone.id] || <MdChair size={11} />,
          clearAction: () => actionCreator && dispatch(actionCreator(null)),
        });
      }
    });

    // Finish selection
    if (customization.finishUrl) {
      items.push({
        id: 'finish',
        category: 'Finish',
        label: 'Wood Finish',
        imageUrl: customization.finishUrl,
        tabId: 'finish',
        icon: <MdFormatPaint size={11} />,
        clearAction: () => dispatch(setFinishUrl(null)),
      });
    }

    // Pillow selections
    if (customization.pillowFile) {
      const meta = [
        customization.pillowSize?.toUpperCase(),
        customization.pillowCount ? `×${customization.pillowCount}` : null,
      ]
        .filter(Boolean)
        .join(' · ');

      let fileUrl: string | undefined;
      try {
        fileUrl = URL.createObjectURL(customization.pillowFile);
      } catch { /* ignore */ }

      items.push({
        id: 'pillow',
        category: 'Pillow',
        label: 'Pillow',
        isFile: true,
        filePreviewUrl: fileUrl,
        meta: meta || undefined,
        tabId: 'pillow',
        icon: <GiPillow size={11} />,
        clearAction: () => {
          dispatch(setPillowFile(null));
          dispatch(setPillowSize(null));
          dispatch(setPillowCount(null));
        },
      });
    }

    // Pillow fabric
    if (customization.pillowFabricUrl) {
      items.push({
        id: 'pillow-fabric',
        category: 'Pillow',
        label: 'Pillow Fabric',
        imageUrl: customization.pillowFabricUrl,
        tabId: 'pillow',
        icon: <GiPillow size={11} />,
        clearAction: () => dispatch(setPillowFabricUrl(null)),
      });
    }

    // Nailhead selections
    if (customization.nailheadFile) {
      let fileUrl: string | undefined;
      try {
        fileUrl = URL.createObjectURL(customization.nailheadFile);
      } catch { /* ignore */ }

      items.push({
        id: 'nailhead',
        category: 'Nailhead',
        label: 'Nailhead',
        isFile: true,
        filePreviewUrl: fileUrl,
        meta: customization.nailheadSize || undefined,
        tabId: 'stud',
        icon: <MdBorderOuter size={11} />,
        clearAction: () => {
          dispatch(setNailheadFile(null));
          dispatch(setNailheadSize(null));
        },
      });
    }

    return items;
  }, [fabricZones, customization, dispatch]);

  const handlePreview = (item: SelectionItem) => {
    const url = item.imageUrl || item.filePreviewUrl;
    if (url) {
      setPreviewUrl(url);
      setPreviewLabel(item.label);
    }
  };

  if (selections.length === 0) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: THEME.primary, boxShadow: `0 0 8px ${THEME.primary}` }}
            />
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: THEME.textMuted }}>
              Your Selections
            </span>
          </div>
          <span
            className="text-[10px] font-semibold tabular-nums"
            style={{ color: THEME.primaryLight }}
          >
            {selections.length}
          </span>
        </div>

        {/* Selection badges - horizontal scrollable */}
        <div className="flex gap-2 overflow-x-auto pb-2 selections-summary-scroller">
          <AnimatePresence mode="popLayout">
            {selections.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.8, x: -10 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.8, x: -10 }}
                transition={SPRING.snappy}
                className="flex items-center gap-1.5 py-1.5 pl-1.5 pr-1.5 rounded-full shrink-0 group/badge"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${THEME.primaryBorder}`,
                }}
              >
                {/* Thumbnail - clickable for preview */}
                <button
                  onClick={() => handlePreview(item)}
                  className="relative cursor-pointer shrink-0 group/thumb"
                  title="Click to preview"
                >
                  {item.imageUrl ? (
                    <div
                      className="w-7 h-7 rounded-full overflow-hidden"
                      style={{
                        border: `1.5px solid ${THEME.primaryBorder}`,
                        boxShadow: `0 0 8px rgba(139, 92, 246, 0.2)`,
                      }}
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.label}
                        className="w-full h-full object-cover transition-all duration-200 group-hover/thumb:brightness-75"
                      />
                    </div>
                  ) : item.filePreviewUrl ? (
                    <div
                      className="w-7 h-7 rounded-full overflow-hidden"
                      style={{
                        border: `1.5px solid ${THEME.primaryBorder}`,
                        boxShadow: `0 0 8px rgba(139, 92, 246, 0.2)`,
                      }}
                    >
                      <img
                        src={item.filePreviewUrl}
                        alt={item.label}
                        className="w-full h-full object-cover transition-all duration-200 group-hover/thumb:brightness-75"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        background: THEME.primaryBg,
                        border: `1.5px solid ${THEME.primaryBorder}`,
                      }}
                    >
                      <span style={{ color: THEME.primaryLight }}>{item.icon}</span>
                    </div>
                  )}
                  {/* Eye overlay on hover */}
                  {(item.imageUrl || item.filePreviewUrl) && (
                    <div className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200 bg-black/40">
                      <MdVisibility size={12} className="text-white/90" />
                    </div>
                  )}
                </button>

                {/* Label - clickable to navigate tab */}
                <button
                  onClick={() => onNavigateTab?.(item.tabId)}
                  className="flex flex-col items-start min-w-0 cursor-pointer"
                >
                  <span
                    className="text-[9px] font-semibold leading-none truncate max-w-[72px]"
                    style={{ color: 'rgba(255,255,255,0.8)' }}
                  >
                    {item.label}
                  </span>
                  {item.meta && (
                    <span
                      className="text-[8px] font-medium leading-none mt-0.5"
                      style={{ color: THEME.primaryLight }}
                    >
                      {item.meta}
                    </span>
                  )}
                </button>

                {/* Clear button */}
                <motion.button
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.85 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.clearAction();
                  }}
                  className="w-4.5 h-4.5 rounded-full flex items-center justify-center cursor-pointer shrink-0 opacity-50 hover:opacity-100 transition-opacity duration-200 ml-0.5"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                  }}
                  title={`Clear ${item.label}`}
                >
                  <MdClose size={10} className="text-white/70" />
                </motion.button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Preview Modal */}
      <SwatchPreviewModal
        isOpen={!!previewUrl}
        imageUrl={previewUrl || ''}
        label={previewLabel}
        onClose={() => {
          setPreviewUrl(null);
          setPreviewLabel('');
        }}
      />
    </>
  );
};

export default SelectionsSummary;
